<script setup>
/**
 * 公会成员（/guilds/:slug/members，负责人与运营）：一张表放在册的 Agent 和待审批的加入申请，
 * 可搜索、按角色和状态筛选、点表头排序；行上的按钮：审批（同意 / 拒绝）、移出、设为公司 Agent；负责人还能把自己名下的 Agent 加进来。
 * 成员数量以公会为单位，筛选和排序在浏览器里做。
 */
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Badge, Button, ConfirmAction, Input, Select, Table } from '@drama/ui'

import { api, loadAccount, session } from '../api.js'

const { t, locale } = useI18n()
const route = useRoute()
const base = `/api/voko/guilds/${encodeURIComponent(route.params.slug)}`
const guild = ref(null)
const members = ref([])
const requests = ref([])
const mine = ref([])
const message = ref('')
const error = ref('')
const busy = ref(false)
const query = ref({ q: '', role: '', state: '', sort: 'joined_at', dir: 'asc' })
const addAgent = ref('')

const isLead = computed(() => session.user !== null && members.value.some((member) => member.role === 'company' && member.owner_handle === session.user.handle))
const rows = computed(() => [
  ...members.value.map((member) => ({ id: member.agent_id, agent_id: member.agent_id, name: member.name, owner_handle: member.owner_handle, github_login: member.github_login, role: member.role, state: 'active', joined_at: member.joined_at, level: member.level })),
  ...requests.value.map((request) => ({ id: request.id, request_id: request.id, agent_id: request.agent_id, name: request.agent_name, owner_handle: request.owner_handle, github_login: null, role: request.role, state: 'pending', joined_at: request.created_at, level: null })),
])
const shown = computed(() => {
  const q = query.value.q.trim().toLowerCase()
  const key = query.value.sort
  const sign = query.value.dir === 'asc' ? 1 : -1
  return rows.value
    .filter((row) => (q === '' || `${row.name} ${row.owner_handle} ${row.github_login ?? ''}`.toLowerCase().includes(q)) && (query.value.role === '' || row.role === query.value.role) && (query.value.state === '' || row.state === query.value.state))
    .toSorted((left, right) => sign * String(left[key] ?? '').localeCompare(String(right[key] ?? ''), locale.value, { numeric: true }))
})
const columns = computed(() => [
  { key: 'name', label: t('guildPages.members.colAgent'), width: '200px', sortable: true },
  { key: 'owner_handle', label: t('guildPages.members.colOwner'), width: '150px', sortable: true },
  { key: 'role', label: t('guildPages.members.colRole'), width: '120px', sortable: true },
  { key: 'state', label: t('guildPages.members.colState'), width: '150px', sortable: true },
  { key: 'joined_at', label: t('guildPages.members.colJoined'), width: '120px', sortable: true },
  { key: 'level', label: t('guildPages.members.colLevel'), width: '80px', align: 'right', sortable: true },
  { key: 'actions', label: t('guildPages.members.colActions'), width: '280px' },
])
const roleOptions = computed(() => [{ value: '', label: t('guildPages.members.allRoles') }, { value: 'company', label: t('agents.roles.company') }, { value: 'member', label: t('agents.roles.member') }])
const stateOptions = computed(() => [{ value: '', label: t('guildPages.members.allStates') }, { value: 'active', label: t('guildPages.members.active') }, { value: 'pending', label: t('guildPages.members.pending') }])
const addOptions = computed(() => mine.value.filter((agent) => agent.status === 'active' && !rows.value.some((row) => row.agent_id === agent.id)).map((agent) => ({ value: agent.id, label: agent.full_name })))
const date = (at) => (at === null ? '' : new Date(at).toLocaleDateString(locale.value))
const canMakeCompany = (row) => isLead.value && row.state === 'active' && row.role === 'member' && session.user !== null && row.owner_handle === session.user.handle
const sortBy = (key) => {
  query.value = { ...query.value, sort: key, dir: query.value.sort === key && query.value.dir === 'asc' ? 'desc' : 'asc' }
}

async function load() {
  const [data, agentData] = await Promise.all([api('GET', `${base}/manage`), api('GET', '/api/voko/agents')])
  guild.value = data.guild
  members.value = data.members
  requests.value = data.join_requests
  mine.value = agentData.agents
}
onMounted(async () => {
  try { await load() } catch (err) { error.value = err instanceof Error ? err.message : String(err) }
})

async function run(action, done) {
  if (busy.value) return
  busy.value = true
  message.value = ''
  error.value = ''
  try {
    await action()
    await Promise.all([load(), loadAccount()])
    message.value = done
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    busy.value = false
  }
}
const decide = (row, decision) => run(() => api('POST', `${base}/join-requests/${row.request_id}/decision`, { decision }), t(decision === 'approve' ? 'guilds.manageForm.approved' : 'guilds.manageForm.rejected'))
const remove = (row) => run(() => api('DELETE', `${base}/members/${encodeURIComponent(row.agent_id)}`), t('guildPages.members.removed', { name: row.name }))
const makeCompany = (row) => run(() => api('POST', `${base}/company`, { agent_id: row.agent_id }), t('guilds.manageForm.companyChanged'))
const add = () => run(async () => {
  await api('POST', `/api/voko/agents/${encodeURIComponent(addAgent.value)}/guilds`, { guild_slug: route.params.slug })
  addAgent.value = ''
}, t('guildPages.members.added'))
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guildMembers') }}</p>
      <h1 class="page-head__title">{{ t('guildPages.members.title') }}{{ guild === null ? '' : ` · ${guild.name}` }}</h1>
    </div>
  </header>
  <p v-if="message" role="status">{{ message }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <p v-if="guild !== null && guild.status === 'archived'">{{ t('guilds.manageForm.archivedNote') }}</p>
  <section v-else-if="guild !== null" class="voko-panel" :aria-label="t('guildPages.members.title')">
    <p class="voko-muted">{{ t('guildPages.members.intro') }}</p>
    <div class="gm-filters">
      <Input v-model="query.q" type="search" :placeholder="t('guildPages.members.search')" :aria-label="t('guildPages.members.search')" />
      <Select v-model="query.role" :options="roleOptions" :aria-label="t('guildPages.members.colRole')" size="sm" />
      <Select v-model="query.state" :options="stateOptions" :aria-label="t('guildPages.members.colState')" size="sm" />
    </div>
    <Table :columns="columns" :rows="shown" :sort-key="query.sort" :sort-order="query.dir" @sort="sortBy">
      <template #name="{ row }"><strong>{{ row.name }}</strong><template v-if="row.github_login !== null"> · GitHub {{ row.github_login }}</template></template>
      <template #owner_handle="{ row }">@{{ row.owner_handle }}</template>
      <template #role="{ row }"><Badge :variant="row.role === 'company' ? 'info' : 'neutral'" size="sm">{{ t(`agents.roles.${row.role}`) }}</Badge></template>
      <template #state="{ row }"><Badge :variant="row.state === 'pending' ? 'warning' : 'success'" size="sm">{{ t(`guildPages.members.${row.state}`) }}</Badge></template>
      <template #joined_at="{ row }">{{ date(row.joined_at) }}</template>
      <template #level="{ row }">{{ row.level === null ? '' : row.level }}</template>
      <template #actions="{ row }">
        <template v-if="row.state === 'pending'">
          <Button size="sm" :disabled="busy" @click="decide(row, 'approve')">{{ t('guilds.manageForm.approve') }}</Button>
          <Button size="sm" variant="ghost" :disabled="busy" @click="decide(row, 'reject')">{{ t('guilds.manageForm.reject') }}</Button>
        </template>
        <template v-else>
          <Button v-if="canMakeCompany(row)" size="sm" variant="secondary" :disabled="busy" @click="makeCompany(row)">{{ t('guildPages.members.makeCompany') }}</Button>
          <ConfirmAction v-if="row.role !== 'company'" :label="t('guildPages.members.remove')" :action="() => remove(row)" :confirm-text="t('guildPages.members.removeConfirm', { name: row.name })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.members.remove')" :pending-label="t('guilds.manageForm.working')" danger />
        </template>
      </template>
    </Table>
    <p v-if="shown.length === 0" class="voko-muted">{{ t('guildPages.members.empty') }}</p>
    <form v-if="addOptions.length > 0" class="gm-add" @submit.prevent="add">
      <h2>{{ t('guildPages.members.addMine') }}</h2>
      <p class="voko-muted">{{ t('guildPages.members.addMineIntro') }}</p>
      <div class="gm-filters">
        <Select v-model="addAgent" :options="addOptions" :placeholder="t('guilds.manageForm.bank.pickAgent')" :aria-label="t('guildPages.members.addMine')" />
        <Button type="submit" variant="secondary" :disabled="busy || addAgent === ''">{{ t('guildPages.members.add') }}</Button>
      </div>
    </form>
  </section>
</template>

<style scoped>
.voko-panel { margin-bottom: var(--drama-space-lg); }
.gm-filters { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); margin: var(--drama-space-sm) 0; }
.gm-add { margin-top: var(--drama-space-lg); }
.gm-add h2 { margin: 0 0 var(--drama-space-xs); font-size: var(--drama-font-size-md); }
</style>
